import React from 'react';
import { Sparkles, Check, ArrowRight } from 'lucide-react';

/**
 * Free → paid temptation surface for the Cory panel.
 * Shows entry-plan users how much of their free allowance is left and exactly
 * what a paid plan unlocks, with a one-tap upgrade CTA. Renders nothing for
 * users already on a paid tier.
 */
export const CoryUpgradeTeaser = ({ status, onUpgrade, variant = 'panel' }) => {
  if (!status || status.is_paid) return null;

  const { used = 0, limit, remaining, pro_benefits = [] } = status;
  const trialPro = !!(status.is_trial && status.cory_pro_via_trial);
  const hasCap = typeof limit === 'number' && limit > 0;
  const pct = hasCap ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const low = hasCap && remaining !== null && remaining <= 5;

  const benefits = (pro_benefits.length ? pro_benefits : [
    'Unlimited Cory conversations',
    'Deeper analysis of your numbers',
    'Premium intelligence modules',
  ]).slice(0, variant === 'inline' ? 5 : 3);

  return (
    <div
      data-testid="cory-upgrade-teaser"
      className="rounded-2xl border border-indigo-200/70 bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]"
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0">
          <Sparkles className="w-3.5 h-3.5 text-white" />
        </div>
        <span className="text-sm font-semibold text-slate-900">
          {trialPro ? 'Keep CoryPro after your trial' : 'Unlock Cory Pro'}
        </span>
      </div>

      {hasCap && (
        <div className="mb-3" data-testid="cory-usage-meter">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className={low ? 'text-rose-600 font-medium' : 'text-slate-500'}>
              {remaining} of {limit} free questions left this month
            </span>
            <span className="text-slate-400" aria-hidden="true">{used}/{limit}</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${low ? 'bg-rose-500' : 'bg-gradient-to-r from-indigo-500 to-purple-600'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      <p className="text-[11px] text-slate-500 mb-2">
        {trialPro
          ? "You've got CoryPro free during your trial. Keep these when it ends:"
          : low
            ? "You're almost out — upgrade to keep the answers coming:"
            : "On a paid plan you'd also get:"}
      </p>
      <ul className="space-y-1.5 mb-3">
        {benefits.map((b) => (
          <li key={b} className="flex items-start gap-2 text-[12px] text-slate-700">
            <Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        data-testid="cory-upgrade-cta"
        onClick={onUpgrade}
        className="w-full flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold py-2.5 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-indigo-500/30"
      >
        {trialPro ? 'Keep CoryPro' : 'Upgrade to Pro'}
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
};

export default CoryUpgradeTeaser;
